import type { PsychologistUpdateData } from '@/types/psychologist';

/**
 * Pure helpers behind the profile fields the customize modal edits inline.
 * Kept out of the components so the rules stay unit-testable — the suite is
 * `environment: 'node'` and cannot render.
 */

/**
 * Mirrors the API's own CRP regex
 * (`api/src/modules/psychologist/psychologist.schemas.ts`), which ships without
 * an error message and would otherwise surface as a bare 400.
 */
export const CRP_PATTERN = /^(?:CRP\s*)?\d{2}\/\d{4,6}$/;

export function validateFullName(draft: PsychologistUpdateData): string | null {
    return draft.full_name?.trim() ? null : 'Informe seu nome.';
}

export function validateCrp(draft: PsychologistUpdateData): string | null {
    const crp = draft.crp?.trim() ?? '';

    // Clearing the CRP is legitimate: the API nulls an empty string and the
    // visibility toggle simply goes back to disabled.
    if (!crp) return null;

    return CRP_PATTERN.test(crp) ? null : 'Formato de CRP inválido. Use o formato 00/00000.';
}

/** "06/12345 · SP", or just the number when no UF is on file. */
export function formatCrpDisplay(crp?: string, crpState?: string): string {
    if (!crp) return '';
    return [crp, crpState].filter(Boolean).join(' · ');
}

const getErrorStatus = (error: unknown): number | undefined => {
    if (!error || typeof error !== 'object') return undefined;

    return (error as { response?: { status?: number } }).response?.status;
};

/**
 * Maps by status rather than reading the API's `message`.
 *
 * Both failure modes here answer in English — 409 with "CRP already in use" and
 * 400 with Fastify's `body/crp must match pattern …` — so passing the payload
 * through `extractApiErrorMessage` would put either straight in front of a
 * pt-BR user.
 */
export function profileSaveErrorMessage(error: unknown): string {
    switch (getErrorStatus(error)) {
        case 409:
            return 'Este CRP já está cadastrado em outra conta.';
        case 400:
            return 'Não foi possível salvar: verifique o valor informado.';
        case 404:
            return 'Sessão expirada. Recarregue a página.';
        default:
            return 'Não foi possível salvar. Tente de novo.';
    }
}
